export const ROLE = {
  USER: 'ROLE_USER',
  ADMIN: 'ROLE_ADMIN',
  ADMIN_TENANT: 'ROLE_ADMIN_TENANT',
  MANAGER: 'ROLE_MANAGER',
  SYSTEM_ADMIN: 'ROLE_SYSTEM_ADMIN',
};

export const ROLES = Object.values(ROLE);

export const DEFAULT_ROLE = ROLE.USER;

export const isAdminTenant = (role) => {
  switch (role) {
    case ROLE.SYSTEM_ADMIN:
    case ROLE.ADMIN:
    case ROLE.ADMIN_TENANT:
      return true;
    default:
      return false;
  }
};

export const isAdmin = (role) => {
  switch (role) {
    case ROLE.SYSTEM_ADMIN:
    case ROLE.ADMIN:
      return true;
    default:
      return false;
  }
};

export const isManager = (role) => {
  switch (role) {
    case ROLE.SYSTEM_ADMIN:
    case ROLE.ADMIN:
    case ROLE.ADMIN_TENANT:
    case ROLE.MANAGER:
      return true;
    default:
      return false;
  }
};

export const isUser = (role) => {
  switch (role) {
    case ROLE.MANAGER:
    case ROLE.USER:
      return true;
    default:
      return false;
  }
};

// Roles that should be visually flagged as "administrator accounts" when the
// admin UI can't act on them. Drives tooltip selection on the user list.
// Note: this is purely a labelling concern. The actual authorization rules
// (which targets a caller can edit/delete) live in `canEditRole`/`canDeleteRole`.
const PROTECTED_TARGETS = [ROLE.ADMIN, ROLE.SYSTEM_ADMIN, ROLE.ADMIN_TENANT];

// Top-tier cross-tenant roles where peer-on-peer action is forbidden, to prevent
// a "two admins mutually demote, nobody left" lockout. Standard pattern in cloud
// admin tooling (AWS root, GCP org admin, Okta super admin, GitHub Enterprise).
// Note: ADMIN_TENANT isn't here because it's already protected from edit via
// `grantableRoles` (nobody can grant it), and it's a singleton per tenant anyway.
const PEER_LOCKED_ROLES = [ROLE.ADMIN, ROLE.SYSTEM_ADMIN];

/** Whether this role is shielded from deletion via the admin UI, regardless of caller rank. */
export const isProtectedRole = (role) => PROTECTED_TARGETS.includes(role);

// Roles that carry broad permissions across the tenant — UI should warn and ask
// for confirmation before granting them via invite or role edit.
const PRIVILEGED_ROLES = [ROLE.ADMIN_TENANT, ROLE.ADMIN, ROLE.SYSTEM_ADMIN];

/** Whether granting this role warrants an extra confirmation step in the UI. */
export const isPrivilegedRole = (role) => PRIVILEGED_ROLES.includes(role);

/**
 * Whether `callerRole` is allowed to edit a user holding `targetRole`. Combines
 * the backend's "caller must be able to grant the current role" rule with the
 * FE-side guard against peer demotion at the top tier (where no higher tier can
 * recover from a mutual demotion). Server remains the source of truth — this is
 * purely UX. Callers are still responsible for the identity-level "no self-edit"
 * check, which depends on user id rather than role.
 */
export const canEditRole = (callerRole, targetRole) => {
  if (!grantableRoles(callerRole).includes(targetRole)) return false;
  if (PEER_LOCKED_ROLES.includes(targetRole) && callerRole === targetRole) return false;
  return true;
};

// The backend converged delete authorization onto the same matrix as edit-role:
// same grantable-target rule, same top-tier peer block, same "no self" rule
// (enforced at the identity layer by callers). Aliased rather than duplicated so
// the two stay in sync if the policy ever evolves.
export const canDeleteRole = canEditRole;

// Roles that can be ASSIGNED to a user (via invite OR role edit). The broader
// policy — used by the role-edit flow. Purely a UX filter so users don't pick a
// role the backend would 403 on — the server remains the source of truth.
// ROLE_ADMIN_TENANT is intentionally excluded from every set: it's a singleton
// per tenant, assigned at tenant creation, and not grantable via this UI.
export const grantableRoles = (role) => {
  switch (role) {
    case ROLE.SYSTEM_ADMIN:
      return [ROLE.SYSTEM_ADMIN, ROLE.ADMIN, ROLE.MANAGER, ROLE.USER];
    case ROLE.ADMIN:
      return [ROLE.ADMIN, ROLE.MANAGER, ROLE.USER];
    case ROLE.ADMIN_TENANT:
    case ROLE.MANAGER:
      return [ROLE.MANAGER, ROLE.USER];
    default:
      return [];
  }
};

// Cross-tenant roles that should never be granted via an invite link — they must
// be reached by promoting an existing, known user through the role-edit flow.
const NON_INVITABLE = [ROLE.ADMIN, ROLE.SYSTEM_ADMIN];

// Roles grantable on a NEW invitation. Tighter than `grantableRoles` (the role-edit
// policy) by excluding cross-tenant roles — they span every tenant, so promoting
// an existing, known user is safer than minting a brand-new account at that level
// through an invite link. Derived from `grantableRoles` so the two policies stay
// in sync as the hierarchy evolves.
export const invitableRoles = (role) => {
  return grantableRoles(role).filter((target) => !NON_INVITABLE.includes(target));
};
